/**
 * `/$tenantSlug/settings` — settings landing.
 *
 * Per KEN_LAYER_7 §7.7. Shell-owned sub-routes (profile, security, team,
 * rbac, modules, billing) plus module-contributed `/settings/<module>/*`.
 *
 * Skeleton: lists the categories with placeholder copy.
 */

import { usePermission } from "@ken/auth";
import { createFileRoute, Link, Outlet, useLocation, useParams } from "@tanstack/react-router";
import { Building2, CalendarClock, ListChecks, ListOrdered, Lock, Plug, Receipt, Settings as SettingsIcon, TextCursorInput, User, Users } from "lucide-react";
import type { ReactElement, ReactNode } from "react";

import { Card, CardContent, CardHeader, CardTitle } from "@ken/ui";

export const Route = createFileRoute("/$tenantSlug/settings")({
  component: SettingsRoute,
});

interface SettingItem {
  to: string;
  icon: ReactNode;
  title: string;
  description: string;
  /** Optional permission slug gating this card. */
  requires?: string;
}

const ITEMS: SettingItem[] = [
  {
    to: "/$tenantSlug/settings/organization",
    icon: <Building2 size={18} />,
    title: "Organization",
    description:
      "Legal name, addresses, contact, tax, and the defaults on every document.",
    requires: "shell.settings.view",
  },
  {
    to: "/$tenantSlug/settings/profile",
    icon: <User size={18} />,
    title: "Tenant profile",
    description: "Business name, contact, branding.",
  },
  {
    to: "/$tenantSlug/settings/security",
    icon: <Lock size={18} />,
    title: "Security",
    description: "Sign-in, session policies.",
  },
  {
    to: "/$tenantSlug/settings/team",
    icon: <Users size={18} />,
    title: "Team",
    description: "Users + their role assignments.",
  },
  {
    to: "/$tenantSlug/settings/rbac",
    icon: <SettingsIcon size={18} />,
    title: "Roles &amp; permissions",
    description: "Role catalogue + per-user overrides.",
  },
  {
    to: "/$tenantSlug/settings/modules",
    icon: <Plug size={18} />,
    title: "Modules",
    description: "Enable or disable modules for this tenant.",
  },
  {
    to: "/$tenantSlug/settings/billing",
    icon: <Receipt size={18} />,
    title: "Billing",
    description: "Plan, invoices, payment method.",
  },
  {
    to: "/$tenantSlug/settings/terms",
    icon: <CalendarClock size={18} />,
    title: "Payment terms",
    description:
      "Reusable payment-term profiles (Net 30, 2/10 Net 30, Due 15th).",
    // SS-B5 (ADR-0276): Payments owns terms; its page and API check this permission.
    requires: "payments.terms.view",
  },
  {
    // HF-W0b-07: ONE card for all four managed lists. Each list's section only
    // appears for a tenant whose owning module is enabled, so the card is the
    // same for every tenant and the page composes itself.
    to: "/$tenantSlug/settings/lists",
    icon: <ListChecks size={18} />,
    title: "Lists",
    description:
      "The short lists records are sorted by. Rename a value, merge two, retire one.",
    requires: "shell.settings.view",
  },
  {
    // SS-B8-T1 (ADR-0275 point 6): the company's choices about its fields.
    to: "/$tenantSlug/settings/fields",
    icon: <TextCursorInput size={18} />,
    title: "Fields",
    description:
      "Rename, hide or reorder the fields on your forms and record screens.",
    requires: "shell.settings.view",
  },
  {
    // SS-B8-T1: the starting lists switched-on modules bring.
    to: "/$tenantSlug/settings/module-lists",
    icon: <ListOrdered size={18} />,
    title: "Module lists",
    description:
      "Rename, hide or reorder the starting lists your modules bring, such as contact roles.",
    requires: "shell.settings.view",
  },
];

function SettingsRoute(): ReactElement {
  const { tenantSlug } = useParams({ from: "/$tenantSlug" });
  const location = useLocation();
  // Resolve the gated permissions once (hooks can't run inside .map).
  const canViewSettings = usePermission("shell.settings.view");
  const permitted: Record<string, boolean> = {
    "shell.settings.view": canViewSettings,
  };
  const items = ITEMS.filter(
    (item) => !item.requires || permitted[item.requires],
  );
  // Show the category cards only when we are at the exact /settings URL.
  // Sub-routes (/settings/modules, /settings/rbac, etc.) render through
  // the <Outlet/>.
  const onSettingsRoot = location.pathname.endsWith("/settings") ||
    location.pathname.endsWith("/settings/");

  if (!onSettingsRoot) {
    return <Outlet />;
  }

  return (
    <div className="flex flex-col gap-6">
      <header>
        <div className="text-[12px] uppercase tracking-[0.08em] text-[var(--color-text-faint)] font-semibold">
          Tenant
        </div>
        <h1 className="text-[28px] font-bold tracking-tight">Settings</h1>
      </header>

      <div className="grid grid-cols-2 gap-3">
        {items.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            params={{ tenantSlug }}
            data-testid={`shell-settings-item-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className="block"
          >
            <Card surface="glass-strong" className="hover:translate-y-[-1px] transition-transform">
              <CardHeader>
                <div className="flex items-center gap-2.5">
                  <div className="grid place-items-center w-9 h-9 rounded-[var(--radius-md)] bg-[var(--color-glass-surface-strong)] border border-[var(--color-glass-border)] text-[var(--color-text-soft)]">
                    {item.icon}
                  </div>
                  <CardTitle>{item.title}</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-[var(--color-text-soft)]">{item.description}</p>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}
